// What a walkthrough scene is: a price that moves over time, the orders on
// it, and the events (fills, alerts) found where the price crosses them. The
// scene is pure data and functions of time; `LiveCard` draws it.

/** Colour roles (CSS variables from `VizRoot`). */
export type Tone = "up" | "accent" | "stop" | "muted" | "ink";
export const TONE: Record<Tone, string> = {
  up: "var(--viz-up)",
  accent: "var(--viz-accent)",
  stop: "var(--viz-stop)",
  muted: "var(--viz-ink-3)",
  ink: "var(--viz-ink-2)",
};

export interface LevelView {
  key: string;
  value: number;
  tone: Tone;
  /**
   * pending: an open order (solid, coloured); filled: done (faint, dashed,
   * a check on its label); empty: a grid line without an order; line: a
   * reference line (the average entry).
   */
  state: "pending" | "filled" | "empty" | "line";
  /** Shown on the right axis before the price (plain words). */
  name?: string;
  /** The trader's short form for a phone's narrow axis ("TP", "SL"). */
  short?: string;
  /** 0..1: the line draws in, the label fades in. */
  shown: number;
  /** 0..1: fades a level out (it does not retract). Default 1. */
  fade?: number;
}

export interface FillEvent {
  t: number;
  value: number;
  tone: Tone;
  /** The level that filled (its line flashes). */
  level: string;
  /** A letter in the badge (grid: B / S). */
  letter?: string;
  /** A "+$x" chip floats up from the badge. */
  profit?: string;
}

export interface Marker {
  t: number;
  value: number;
}

export interface Stat {
  label: string;
  value: string;
  positive?: boolean;
}

export interface StripItem {
  label?: string;
  value?: string;
  /** A small green tag after the value (LONG). */
  tag?: string;
}

export interface Story {
  /** When each step starts (s); the first is 0. */
  H: number[];
  /** The loop's length (s); the chart fades out over its last moment. */
  loop: number;
  /** The price at time t (any t, including before 0: the past). */
  price: (t: number) => number;
  /** The price range the plot shows (at its widest). */
  range: [number, number];
  /**
   * The range at time t, when the plot rescales as a level moves away (a
   * take-profit coming down), so no band stays empty; within `range`.
   * `past`: how many seconds of the past the plot shows (Infinity when it
   * shows all of it).
   */
  rangeAt?: (t: number, past: number) => [number, number];
  format: (value: number) => string;
  levels: (t: number) => LevelView[];
  fills: FillEvent[];
  /** Alert markers (a bell on the line). */
  markers?: Marker[];
  /** The trade's result: a chip above this fill, from its time on. */
  result?: { fill: number; text: string };
  /** A short note next to a level's label for a while (SL → breakeven). */
  note?: { level: string; from: number; to: number; text: string };
  header: {
    pair: string;
    coin: { glyph: string; color: string };
    meta: string;
  };
  status: (t: number) => { text: string; closed?: boolean };
  stats: (t: number) => Stat[];
  strip?: {
    icon: "radio" | "bell";
    title: string;
    items: StripItem[];
    /** When each item appears (s). */
    at: number[];
  };
}

/** Seconds the step's words lead its event on the chart. */
export const LEAD = 0.3;
/** The fade at the end of the loop (and back in at its start). */
export const FADE_OUT = 0.45;
export const FADE_IN = 0.35;

/** Each step's final frame: where reduced motion rests, and a held step shows. */
export const restFrames = (story: Story): number[] =>
  story.H.map((_, i) =>
    i < story.H.length - 1
      ? story.H[i + 1] - 0.05
      : story.loop - FADE_OUT - 0.25
  );

export const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
export const easeOut = (x: number): number => 1 - (1 - clamp01(x)) ** 3;
export const easeInOut = (x: number): number => {
  const k = clamp01(x);
  return k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2;
};

/** The step (0-based) the playhead is in. */
export const stepAt = (story: Story, t: number): number => {
  let step = 0;
  story.H.forEach((h, i) => {
    if (t >= h - 1e-6) step = i;
  });
  return step;
};
